import { useEffect, useMemo } from 'react';
import { useAuth } from 'src/auth';
import {
  useCreateApplicationMutation,
  useDeleteApplicationMutation,
  useGetAllApplicationsQuery,
  useUpdateApplicationMutation,
} from 'src/services/switch-rest-api/application.service';
import {
  ALL_SELECTION_ID,
  isAllSelection,
  toScopedId,
} from 'src/utils/selection';
import { Application } from 'switch-shared/modules/crud/application/application.entity';

import useEffectiveApplicationGroupId from './useEffectiveApplicationGroupId';
import useEffectiveApplicationId from './useEffectiveApplicationId';
import { useSelectedApplicationId } from './useSelectedApplicationId';

/**
 * Custom hook to get applications for the current group using RTK Query.
 * Provides automatic caching and replaces the old Redux state approach.
 * Also includes CRUD mutation states for create, update, and delete operations.
 *
 * @returns Object containing applications array, loading states, and mutation states
 */
export const useApplications = ({
  skip = false,
  syncSelection = true,
  regex,
}: {
  skip?: boolean;
  syncSelection?: boolean;
  /** Server-side name search for pickers; only affects `searchedApplications`, never the selection-sync effects. */
  regex?: string;
} = {}) => {
  const { isAuthenticated } = useAuth();
  const groupId = useEffectiveApplicationGroupId();
  const scopedGroupId = toScopedId(groupId);
  const appId = useEffectiveApplicationId();
  const { setSelectedApplicationId } = useSelectedApplicationId();

  // Use RTK Query for applications - provides automatic caching
  const {
    data: applicationsData,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
  } = useGetAllApplicationsQuery(
    { applicationGroupId: scopedGroupId },
    { skip: !isAuthenticated || !scopedGroupId || skip }
  );

  // Regex-keyed variant for search-as-you-type pickers. With an empty regex it
  // shares the cache entry of the query above, so it costs nothing extra. The
  // selection-sync effects below must keep reading the unfiltered list; a
  // narrowed result would make them think the selected application vanished.
  const { data: searchedApplicationsData, isFetching: isSearchFetching } =
    useGetAllApplicationsQuery(
      { applicationGroupId: scopedGroupId, ...(regex && { regex }) },
      { skip: !isAuthenticated || !scopedGroupId || skip }
    );

  // Get mutation states (but don't return the mutation functions themselves)
  const [, createMutationState] = useCreateApplicationMutation({
    fixedCacheKey: 'create-application',
  });
  const [, updateMutationState] = useUpdateApplicationMutation({
    fixedCacheKey: 'update-application',
  });
  const [, deleteMutationState] = useDeleteApplicationMutation({
    fixedCacheKey: 'delete-application',
  });

  // Extract applications from RTK Query response
  const applications = useMemo(() => {
    if (isAllSelection(groupId)) return [] as Application[];
    if (!applicationsData?.data) return [] as Application[];
    return applicationsData.data;
  }, [applicationsData, groupId]);

  const searchedApplications = useMemo(() => {
    if (isAllSelection(groupId)) return [] as Application[];
    if (!searchedApplicationsData?.data) return [] as Application[];
    return searchedApplicationsData.data;
  }, [searchedApplicationsData, groupId]);

  const applicationsCount = isAllSelection(groupId)
    ? 0
    : (applicationsData?.totalRecords ?? applicationsData?.data?.length ?? 0);

  useEffect(() => {
    if (!syncSelection) return;
    if (!groupId) return;

    if (isAllSelection(groupId)) {
      if (!isAllSelection(appId)) {
        setSelectedApplicationId(ALL_SELECTION_ID);
      }
    }
  }, [appId, groupId, setSelectedApplicationId, syncSelection]);

  useEffect(() => {
    if (!syncSelection) return;
    if (isLoading || isFetching) return;
    if (!isAllSelection(appId)) return;
    if (applications.length !== 1) return;

    setSelectedApplicationId(applications[0]._id);
  }, [
    applications,
    isLoading,
    isFetching,
    appId,
    setSelectedApplicationId,
    syncSelection,
  ]);

  useEffect(() => {
    if (!syncSelection) return;
    if (!isSuccess || isLoading || isFetching) return;
    if (!appId || isAllSelection(appId)) return;
    if (isAllSelection(groupId)) return;

    const selectedApplicationExists = applications.some(
      (application) => application._id === appId
    );

    if (!selectedApplicationExists) {
      setSelectedApplicationId(ALL_SELECTION_ID);
    }
  }, [
    applications,
    appId,
    groupId,
    isSuccess,
    isLoading,
    isFetching,
    setSelectedApplicationId,
    syncSelection,
  ]);

  return {
    applications,
    searchedApplications,
    isSearchFetching,
    applicationsCount,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    refetch,
    // Mutation states for CRUD operations
    isCreating: createMutationState.isLoading,
    isUpdating: updateMutationState.isLoading,
    isDeleting: deleteMutationState.isLoading,
    createError: createMutationState.error,
    updateError: updateMutationState.error,
    deleteError: deleteMutationState.error,
  };
};

export default useApplications;
